import { PhotoPopupData } from "../interfaces/dto";

const { findHtmlEl, findHtmlEls } = pm.utils.dom;
const { camelCaseToKebabCase, kebabCaseToCamelCase } = pm.utils.str;

export default function renderPopupPhotoData(
	popup: HTMLElement,
	popupData: PhotoPopupData,
) {
	const isVideo = popupData.videoUrl ? true : false;

	if (isVideo) {
		const imageWrapper = findHtmlEl(popup, ".single-image-wrapper");

		const authorEl = findHtmlEl(imageWrapper, ".gallery-author");
		if (authorEl) authorEl.parentElement?.removeChild(authorEl);

		const nicenameEl = findHtmlEl(imageWrapper, ".gallery-instagram");
		if (nicenameEl) nicenameEl.parentElement?.removeChild(nicenameEl);

		const metadataEl = findHtmlEl(imageWrapper, ".gallery-metadata");
		if (metadataEl) metadataEl.parentElement?.removeChild(metadataEl);

		const locationEl = findHtmlEl(imageWrapper, ".gallery-location");
		if (locationEl) locationEl.parentElement?.removeChild(locationEl);
	}

	const awardsContainer = findHtmlEl(
		popup,
		".awards-section .wpbf-container-center.wpbf-medium-2-3",
	);

	if (!popupData.isPhotoAuthor && !popupData.hasDescription) {
		popup.classList.add("no-description");

		if (awardsContainer) {
			awardsContainer.classList.add("wpbf-padding-medium");
		}
	} else {
		popup.classList.remove("no-description");

		if (awardsContainer) {
			awardsContainer.classList.add("wpbf-padding-medium-top");
		}
	}

	const reportButton = findHtmlEl(popup, ".report-photo-button");

	if (reportButton && !popupData.isLoggedIn) {
		reportButton.parentElement?.removeChild(reportButton);
	}

	for (const key in popupData) {
		if (popupData.hasOwnProperty(key)) {
			const value = popupData[key];
			const kebabCaseKey = camelCaseToKebabCase(key);

			renderTextModel(popup, kebabCaseKey, value, false);
			renderTextModel(popup, kebabCaseKey, value, true);
		}
	}

	renderAttrModel(popup, popupData);
	renderAttrModels(popup, popupData);

	renderIfModelValueMatches(popup, popupData);
	renderIfModelIsTrue(popup, popupData);
	renderIfModelIsFalse(popup, popupData);
	renderIfAnyModelsTrue(popup, popupData);
	renderIfAllModelsTrue(popup, popupData);
	renderIfModelEmpty(popup, popupData);
	renderIfModelNotEmpty(popup, popupData);
	addClassIfModelTrue(popup, popupData);
	addClassIfAllModelsTrue(popup, popupData);
}

function renderTextModel(
	popup: HTMLElement,
	dataKey: string,
	dataValue: string,
	useInnerHTML: boolean,
) {
	const els = useInnerHTML
		? findHtmlEls(popup, `[data-content-model="${dataKey}"]`)
		: findHtmlEls(popup, `[data-text-model="${dataKey}"]`);

	if (els.length === 0) return;

	if (dataKey === "date") {
		const date = new Date(dataValue);
		dataValue = date.toLocaleDateString("en-US", {
			year: "numeric",
			month: "long",
			day: "numeric",
		});
	}

	// Loop using for
	for (let i = 0; i < els.length; i++) {
		const el = els[i];
		let content = dataValue;

		if (!content || content === "0") {
			const defaultValue = el.dataset.defaultValue;

			if (defaultValue) {
				content = defaultValue;
			}
		} else {
			if (el.dataset.textPrefix) {
				content = el.dataset.textPrefix + content;
			}

			if (el.dataset.textSuffix) {
				content = content + el.dataset.textSuffix;
			}
		}

		if (useInnerHTML) {
			el.innerHTML = content;
		} else {
			if (el instanceof HTMLInputElement) {
				el.value = content;
			} else {
				el.textContent = content;
			}
		}

		el.classList.remove("is-waiting");
	}
}

function renderAttrModel(popup: HTMLElement, popupData: PhotoPopupData) {
	const els = findHtmlEls(popup, `[data-attr-model]`);
	if (els.length === 0) return;

	for (let i = 0; i < els.length; i++) {
		const el = els[i];

		const modelKey = el.dataset.attrModel;
		if (!modelKey) continue;

		const attrName = el.dataset.attrName;
		if (!attrName) continue;

		const dataKey = kebabCaseToCamelCase(modelKey);

		if (dataKey in popupData) {
			el.setAttribute(attrName, popupData[dataKey]);
		}
	}
}

function renderAttrModels(popup: HTMLElement, popupData: PhotoPopupData) {
	// The "data-attr-models" contains list of attrName & attrModel pairs.
	const els = findHtmlEls(popup, "[data-attr-models]");
	if (els.length === 0) return;

	for (let i = 0; i < els.length; i++) {
		const el = els[i];
		if (!el.dataset.attrModels) continue;

		const rawData = el.dataset.attrModels;

		// The "dataObj" will be an object containing pairs of attrName & attrModel.
		const dataObj = JSON.parse(rawData);

		// Loop through the object.
		for (const attrName in dataObj) {
			if (!dataObj.hasOwnProperty(attrName)) {
				continue;
			}

			const modelKey = dataObj[attrName];
			const dataKey = kebabCaseToCamelCase(modelKey);

			if (dataKey in popupData) {
				el.setAttribute(attrName, popupData[dataKey]);
			}
		}
	}
}

function renderIfModelValueMatches(
	popup: HTMLElement,
	popupData: PhotoPopupData,
) {
	const selector = makeDataRenderSelector("if-model-key");
	const els = findHtmlEls(popup, selector);
	if (els.length === 0) return;

	for (let i = 0; i < els.length; i++) {
		const el = els[i];

		const modelKey = el.dataset.modelKey;
		const modelValue = el.dataset.renderIfModelValue ?? "";
		let shouldRemove: boolean = true;

		if (modelKey) {
			const dataKey = kebabCaseToCamelCase(modelKey);

			if (dataKey in popupData) {
				if (popupData[dataKey] === modelValue) {
					shouldRemove = false;
				}
			}
		}

		if (shouldRemove) {
			el.parentElement?.removeChild(el);
		}
	}

	els.forEach((el) => el.removeAttribute(dataSelectorToAttr(selector)));
}

function renderIfModelIsTrue(popup: HTMLElement, popupData: PhotoPopupData) {
	const selector = makeDataRenderSelector("if-model-true");
	const els = findHtmlEls(popup, selector);
	if (els.length === 0) return;

	for (let i = 0; i < els.length; i++) {
		const el = els[i];

		const modelKey = el.dataset.renderIfModelTrue;
		let shouldRemove: boolean = true;

		if (modelKey) {
			const dataKey = kebabCaseToCamelCase(modelKey);

			if (dataKey in popupData) {
				if (popupData[dataKey] === true) {
					shouldRemove = false;
				}
			}
		}

		if (shouldRemove) {
			el.parentElement?.removeChild(el);
		}
	}

	els.forEach((el) => el.removeAttribute(dataSelectorToAttr(selector)));
}

function renderIfModelIsFalse(popup: HTMLElement, popupData: PhotoPopupData) {
	const selector = makeDataRenderSelector("if-model-false");
	const els = findHtmlEls(popup, selector);
	if (els.length === 0) return;

	for (let i = 0; i < els.length; i++) {
		const el = els[i];

		const modelKey = el.dataset.renderIfModelFalse;
		let shouldRemove: boolean = true;

		if (modelKey) {
			const dataKey = kebabCaseToCamelCase(modelKey);

			if (dataKey in popupData) {
				if (popupData[dataKey] === false) {
					shouldRemove = false;
				}
			}
		}

		if (shouldRemove) {
			el.parentElement?.removeChild(el);
		}
	}

	els.forEach((el) => el.removeAttribute(dataSelectorToAttr(selector)));
}

function renderIfAnyModelsTrue(popup: HTMLElement, popupData: PhotoPopupData) {
	const selector = makeDataRenderSelector("if-any-models-true");
	const els = findHtmlEls(popup, selector);
	if (els.length === 0) return;

	for (let i = 0; i < els.length; i++) {
		const el = els[i];

		// The value of "attrValue" is comma separated string of model keys.
		let attrValue = el.dataset.renderIfAnyModelsTrue;
		attrValue = attrValue ? attrValue.trim() : "";

		// Split the string into array of model keys.
		const modelKeys = attrValue.split(",");
		let shouldRemove: boolean = true;

		if (modelKeys.length) {
			// Loop through the array of model keys.
			for (let j = 0; j < modelKeys.length; j++) {
				const modelKey = modelKeys[j].trim();
				const dataKey = kebabCaseToCamelCase(modelKey);

				if (dataKey in popupData) {
					// Check if the model key is true.
					if (popupData[dataKey] === true) {
						shouldRemove = false;
						break;
					}
				}
			}
		}

		if (shouldRemove) {
			el.parentElement?.removeChild(el);
		}
	}

	els.forEach((el) => el.removeAttribute(dataSelectorToAttr(selector)));
}

function renderIfAllModelsTrue(popup: HTMLElement, popupData: PhotoPopupData) {
	const selector = makeDataRenderSelector("if-all-models-true");
	const els = findHtmlEls(popup, selector);
	if (els.length === 0) return;

	for (let i = 0; i < els.length; i++) {
		const el = els[i];

		// The value of "attrValue" is comma separated string of model keys.
		let attrValue = el.dataset.renderIfAllModelsTrue;
		attrValue = attrValue ? attrValue.trim() : "";

		// Split the string into array of model keys.
		const modelKeys = attrValue.split(",");
		let shouldRemove: boolean = true;

		if (modelKeys.length) {
			// Loop through the array of model keys.
			for (let j = 0; j < modelKeys.length; j++) {
				const modelKey = modelKeys[j].trim();
				const dataKey = kebabCaseToCamelCase(modelKey);

				if (dataKey in popupData) {
					// Check if the model key is true.
					if (popupData[dataKey] === true) {
						shouldRemove = false;
					} else {
						shouldRemove = true;
						break;
					}
				}
			}
		}

		if (shouldRemove) {
			el.parentElement?.removeChild(el);
		}
	}

	els.forEach((el) => el.removeAttribute(dataSelectorToAttr(selector)));
}

function renderIfModelEmpty(popup: HTMLElement, popupData: PhotoPopupData) {
	const selector = makeDataRenderSelector("if-model-empty");
	const els = findHtmlEls(popup, selector);
	if (els.length === 0) return;

	for (let i = 0; i < els.length; i++) {
		const el = els[i];

		const modelKey = el.dataset.renderIfModelEmpty;
		let shouldRemove: boolean = false;

		if (modelKey) {
			const dataKey = kebabCaseToCamelCase(modelKey);

			if (dataKey in popupData) {
				if (popupData[dataKey]) {
					if (Array.isArray(popupData[dataKey])) {
						if (popupData[dataKey].length) {
							shouldRemove = true;
						}
					} else {
						shouldRemove = true;
					}
				}
			}
		}

		if (shouldRemove) {
			el.parentElement?.removeChild(el);
		}
	}

	els.forEach((el) => el.removeAttribute(dataSelectorToAttr(selector)));
}

function renderIfModelNotEmpty(popup: HTMLElement, popupData: PhotoPopupData) {
	const selector = makeDataRenderSelector("if-model-not-empty");
	const els = findHtmlEls(popup, selector);
	if (els.length === 0) return;

	for (let i = 0; i < els.length; i++) {
		const el = els[i];

		const modelKey = el.dataset.renderIfModelNotEmpty;
		let shouldRemove: boolean = true;

		if (modelKey) {
			const dataKey = kebabCaseToCamelCase(modelKey);

			if (dataKey in popupData) {
				if (popupData[dataKey]) {
					if (Array.isArray(popupData[dataKey])) {
						if (popupData[dataKey].length) {
							shouldRemove = false;
						}
					} else {
						shouldRemove = false;
					}
				}
			}
		}

		if (shouldRemove) {
			el.parentElement?.removeChild(el);
		}
	}

	els.forEach((el) => el.removeAttribute(dataSelectorToAttr(selector)));
}

function addClassIfModelTrue(popup: HTMLElement, popupData: PhotoPopupData) {
	const selector = makeDataAddClassSelector("if-model-true");

	const els = findHtmlEls(popup, selector);
	if (els.length === 0) return;

	const classToAddAttr = "data-class-to-add";

	for (let i = 0; i < els.length; i++) {
		const el = els[i];

		const modelKey = el.dataset.addClassIfModelTrue;
		const className = el.getAttribute(classToAddAttr);
		if (!modelKey || !className) continue;

		const dataKey = kebabCaseToCamelCase(modelKey);

		if (dataKey in popupData) {
			if (popupData[dataKey] === true) {
				el.classList.add(className);
			}
		}
	}

	els.forEach((el) => {
		el.removeAttribute(dataSelectorToAttr(selector));
		el.removeAttribute(classToAddAttr);
	});
}

function addClassIfAllModelsTrue(
	popup: HTMLElement,
	popupData: PhotoPopupData,
) {
	const selector = makeDataAddClassSelector("if-all-models-true");

	const els = findHtmlEls(popup, selector);
	if (els.length === 0) return;

	const classToAddAttr = "data-class-to-add";

	for (let i = 0; i < els.length; i++) {
		const el = els[i];

		// The value of "attrValue" is comma separated string of model keys.
		let attrValue = el.dataset.addClassIfAllModelsTrue;
		attrValue = attrValue ? attrValue.trim() : "";

		// Split the string into array of model keys.
		const modelKeys = attrValue.split(",");
		if (modelKeys.length === 0) continue;

		let shouldAddClass: boolean = false;

		// Loop through the array of model keys.
		for (let j = 0; j < modelKeys.length; j++) {
			const modelKey = modelKeys[j].trim();
			const dataKey = kebabCaseToCamelCase(modelKey);

			if (dataKey in popupData) {
				// Check if the model key is true.
				if (popupData[dataKey] === true) {
					shouldAddClass = true;
				} else {
					shouldAddClass = false;
					break;
				}
			}
		}

		if (shouldAddClass) {
			const classToAdd = el.getAttribute(classToAddAttr);
			if (classToAdd) el.classList.add(classToAdd);
		}
	}

	els.forEach((el) => {
		el.removeAttribute(dataSelectorToAttr(selector));
		el.removeAttribute(classToAddAttr);
	});
}

function makeDataRenderSelector(key: string): string {
	return `[data-render-${key}]`;
}

function makeDataAddClassSelector(key: string): string {
	return `[data-add-class-${key}]`;
}

function dataSelectorToAttr(selector: string): string {
	return selector.replace("[", "").replace("]", "");
}
